import { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  JobSearchFilterToolbar,
  buildJobSortOrder,
  type JobSearchFilterToolbarLabels,
  type ToolbarBadgeProps,
  type ToolbarButtonProps,
} from '@pigeons/shared-search-utility/job';
import { ServiceCategory } from '../types/service';
import { City } from '../types/profile';
import { AlphabeticalSortOption, PublishedAtSortOption } from '../types/search';
import { Button } from '../components/common/Button';
import SearchBar from '../components/common/SearchBar';
import FilterModal from '../components/jobs/JobFilterModal';
import JobSortModal from '../components/jobs/JobSortModal';
import { useJobSearchStore } from '../stores/jobSearchStore';
import { LoadingSpinner } from '../components/LoadingSpinner';
import JobSearchCard from '../components/jobs/JobSearchCard';
import JobDetailsModal from '../components/jobs/JobDetailsModal';
import { Job, JobSearchResult } from '../types/job';
import UpsertQuoteModal from '../components/quotes/UpsertQuoteModal';
import { useProfileStore } from '../stores/profileStore';
import { ROLES } from '../types/auth';
import { useHasPermission } from '../hooks/useHasPermission';
import { useLocation, useNavigate } from 'react-router-dom';
import { JobStatus } from '../types/common';
import { useQuoteStore } from '../stores/quoteStore';
import GenericInformationModal from '../components/common/GenericInformationModal';
import { ListingType } from '../types/job';
import { useJobStore } from '../stores/jobStore';
import Badge from '../components/Badge';
import { languageOptions } from '../utils/languagesUtils';

import { useSearchStateStore } from '../stores/searchStateStore';
import { navigateToJobSearch } from '../utils/jobSearchRouting';

// The shared toolbar hands loosely-typed `variant`/`color`/`size` strings to its slot components;
// narrow them to the values the app's Button and Badge accept.
const button_variants = ['primary', 'alternate', 'transparent-with-border', 'simple-text'] as const;

const JobToolbarButton = ({ className, variant, onClick, children }: ToolbarButtonProps) => {
  const safe_variant = button_variants.find(v => v === variant) ?? 'primary';
  return (
    <Button className={className} variant={safe_variant} onClick={onClick}>
      {children}
    </Button>
  );
};

const JobToolbarBadge = ({ variant, color, size, endIcon, children }: ToolbarBadgeProps) => {
  const safe_variant = variant === 'solid' ? 'solid' : 'light';
  const safe_color =
    color === 'primary' ||
    color === 'success' ||
    color === 'error' ||
    color === 'warning' ||
    color === 'info' ||
    color === 'light'
      ? color
      : 'primary';
  const safe_size = size === 'sm' || size === 'md' ? size : 'md';

  return (
    <Badge variant={safe_variant} color={safe_color} size={safe_size} endIcon={endIcon}>
      {children}
    </Badge>
  );
};

// convert JobSearchResult → Job
const mapJobSearchResultToJob = (job: JobSearchResult): Job => ({
  id: job.id,
  title: job.title,
  description: job.description,
  on_site: job.on_site,
  start_date: job.start_date,
  end_date: job.end_date,
  published_at: job.published_at,
  expires_at: job.expires_at,
  selection_date: job.selection_date,
  status: job.status as JobStatus,
  languages: job.languages,
  visibility: job.visibility,
  city: { id: job.city_id, name: job.city_name },
  client: {
    id: job.client_id,
    first_name: job.client_first_name,
    last_name: job.client_last_name,
    company_name: job.client_company_name || '',
    avatar_url: job.client_avatar_url || '',
  },
  shadow_recruiter_id: job.shadow_recruiter_id ?? null,
  shadow_recruiter_company_name: job.shadow_recruiter_company_name ?? null,
  is_shadow_recruiter: job.is_shadow_recruiter ?? false,
  recruiter_display_name: job.recruiter_display_name ?? null,
  recruiter_display_context: job.recruiter_display_context ?? null,
  budget_range: {
    id: job.budget_range_id,
    code: job.budget_range_code,
    min_amount: job.budget_range_min_amount,
    max_amount: job.budget_range_max_amount,
  },
  required_services: job.service_category_slugs.map(slug => ({
    id: 0,
    label: slug,
    slug: slug,
  })),
  listing_type:
    job.listing_type_id && job.listing_type_code
      ? {
          id: job.listing_type_id,
          code: job.listing_type_code,
        }
      : ({
          id: 1,
          code: 'standard',
        } as ListingType),
  created_at: job.created_at,
  updated_at: job.updated_at,
  completed_at: null,
  quote_requests: [],
  has_user_quote: job.has_user_quote,
});

const JobSearchPage = () => {
  const { t } = useTranslation(['common', 'jobs', 'admin']);
  const navigate = useNavigate();
  const { quotes, createQuote } = useQuoteStore();

  const {
    project,
    setProjectKeyword,
    setProjectLanguages,
    setProjectServices,
    setProjectCities,
    setProjectAlphabeticalSort,
    setProjectPublishedAtSort,
  } = useSearchStateStore();

  const selectedLanguages = project.selectedLanguages;
  const selectedServices = project.selectedServices;
  const selectedCities = project.selectedCities;
  const alphabeticalSort = project.alphabeticalSort;
  const publishedAtSort = project.publishedAtSort;

  const setSelectedLanguages = (languages: string[]) => setProjectLanguages(languages);
  const setSelectedServices = (services: ServiceCategory[]) => setProjectServices(services);
  const setSelectedCities = (cities: City[]) => setProjectCities(cities);
  const setAlphabeticalSort = (sort: AlphabeticalSortOption | null) =>
    setProjectAlphabeticalSort(sort);
  const setPublishedAtSort = (sort: PublishedAtSortOption | null) =>
    setProjectPublishedAtSort(sort);

  const jobSearchToolbarLabels = useMemo(
    (): JobSearchFilterToolbarLabels => ({
      filterBy: t('filterBy', { ns: 'jobs' }),
      sortBy: t('sortBy', { ns: 'jobs' }),
      badgeLanguagePrefix: t('badges.language', { ns: 'common' }),
      badgeServicePrefix: t('badges.service', { ns: 'common' }),
      badgeCityPrefix: t('badges.city', { ns: 'common' }),
      badgeAlphabeticalPrefix: t('badges.alphabetical', { ns: 'common' }),
      badgePublishedAtPrefix: t('badges.publishedAt', { ns: 'common' }),
      sortAlphabeticalLabel: opt => t(`sorts.${opt}`, { ns: 'jobs' }),
      sortPublishedAtLabel: opt => t(`sorts.${opt}`, { ns: 'jobs' }),
    }),
    [t]
  );

  const searchResultsRef = useRef<HTMLDivElement>(null);

  const [openJobDetailsModal, setOpenJobDetailsModal] = useState(false);

  const { cancelJob } = useJobStore();

  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [openApplyModal, setOpenApplyModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [createdQuoteId, setCreatedQuoteId] = useState<string | null>(null);

  const [isCreating, setisCreating] = useState(false);

  const location = useLocation();

  const [showJobCancelConfirmationModal, setShowJobCancelConfirmationModal] = useState(false);

  const createdQuote = quotes.find(quote => quote.id === createdQuoteId);

  const searchParams = new URLSearchParams(window.location.search);
  let userId = searchParams.get('userId');
  if (userId) {
    userId = userId.replace(/\/$/, '');
  }

  // Captured once on mount (not recomputed every render) so that later
  // selecting a job from the list — which also writes `?jobId=` into the URL
  // via navigateToJobSearch, for copy-link — never re-triggers the deep-link
  // "already applied?" redirect below.
  const [sharedJobId] = useState<string | undefined>(() => searchParams.get('jobId') || undefined);

  const {
    results,
    spotlightedResults,
    error,
    spotlightedError,
    hasMore,
    suggestions,
    suggestionsLoading,
    fetch_job_suggestions,
    search_jobs,
    fetch_more_jobs,
    fetch_spotlighted_jobs,
    fetchJobById,
    searchMode,
  } = useJobSearchStore();

  const handleCreateQuote = async () => {
    if (!selectedJob) return;
    // Defensive guard: only freelancers can create quotes. The RPC would reject a
    // non-freelancer anyway, but this avoids firing it even if the Apply button were
    // somehow reachable for a recruiter or admin.
    if (profile?.role !== ROLES.FREELANCER) return;

    try {
      setisCreating(true);
      const quote = await createQuote({
        jobId: selectedJob.id,
      });

      if (quote) {
        setisCreating(false);
        setCreatedQuoteId(quote.id);
        setOpenApplyModal(true);
      }
    } catch (error) {
      setisCreating(false);
      console.error('Error creating quote:', error);
      setShowErrorModal(true);
    }
  };

  const handleCancelJob = async () => {
    try {
      await cancelJob(selectedJob!.id);
      setShowJobCancelConfirmationModal(false);
    } catch (error) {
      console.error('Cancel error:', error);
    }
  };

  const handleRetryCreateQuote = () => {
    setShowErrorModal(false);
    handleCreateQuote();
  };

  const handleSearchSubmit = async () => {
    const state = useSearchStateStore.getState().project;
    const sort = buildJobSortOrder(state.alphabeticalSort, state.publishedAtSort);

    await search_jobs({
      keyword: state.keyword.trim() || undefined,
      filter_languages: state.selectedLanguages,
      filter_city_ids: state.selectedCities.map(city => city.id),
      filter_service_category_slugs: state.selectedServices.map(service => service.slug),
      sort_order: sort,
    });
    if (searchResultsRef.current) {
      searchResultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    const state = useSearchStateStore.getState().project;
    const hasFilters =
      state.keyword.trim() ||
      state.selectedLanguages.length > 0 ||
      state.selectedServices.length > 0 ||
      state.selectedCities.length > 0 ||
      (state.alphabeticalSort && state.alphabeticalSort !== 'az') ||
      (state.publishedAtSort && state.publishedAtSort !== 'publishedAtDesc');

    if (hasFilters && !hasSearched) {
      setHasSearched(true);
      handleSearchSubmit();
    }
    // Intentionally run only on mount to apply URL/initial filters once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const publishedJobs = useMemo(() => {
    const base = searchMode === 'search' ? results : spotlightedResults;

    if (userId) {
      return base.filter(job => job.client_id === userId);
    }

    return base;
  }, [searchMode, results, spotlightedResults, userId]);

  useEffect(() => {
    const searchQuery = (location.state as { searchQuery?: string })?.searchQuery;
    if (!searchQuery) {
      fetch_spotlighted_jobs();
    } else {
      search_jobs({ keyword: searchQuery });
    }
  }, [location.state, fetch_spotlighted_jobs, search_jobs]);

  useEffect(() => {
    if (!sharedJobId) return;
    let cancelled = false;
    (async () => {
      // A deep link to a job (email, notification, copied link) may belong to
      // a freelancer who already applied. Send them to their existing
      // application instead of the generic job board. The RPC fails
      // gracefully (returns undefined) when there's no authenticated user,
      // so this is safe to call unconditionally.
      const existingQuoteId = await useQuoteStore.getState().fetchQuoteIdByJobId(sharedJobId);
      if (cancelled) return;
      if (existingQuoteId) {
        navigate(`/projects/${sharedJobId}?selectedQuoteId=${existingQuoteId}`, {
          replace: true,
        });
        return;
      }

      const fetchedJob = await fetchJobById(sharedJobId);
      if (cancelled || !fetchedJob) return;
      setSelectedJob(mapJobSearchResultToJob(fetchedJob));
      setOpenJobDetailsModal(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [sharedJobId, fetchJobById, navigate]);

  useEffect(() => {
    const hasFilters =
      project.selectedLanguages.length > 0 ||
      project.selectedServices.length > 0 ||
      project.selectedCities.length > 0 ||
      (project.alphabeticalSort && project.alphabeticalSort !== 'az') ||
      (project.publishedAtSort && project.publishedAtSort !== 'publishedAtDesc');

    if (project.keyword.trim() === '' && !hasFilters) {
      setHasSearched(false);
      fetch_spotlighted_jobs();
    }
  }, [
    project.keyword,
    project.selectedLanguages,
    project.selectedServices,
    project.selectedCities,
    project.alphabeticalSort,
    project.publishedAtSort,
    fetch_spotlighted_jobs,
  ]);

  useEffect(() => {
    const projectFromState = location.state?.fullProject;
    if (projectFromState) {
      setSelectedJob(projectFromState);
      setOpenJobDetailsModal(true);
    }
  }, [location.state]);

  const searchError = searchMode === 'search' ? error : spotlightedError;

  const onSelectjob = (job: JobSearchResult) => {
    setSelectedJob(mapJobSearchResultToJob(job));
    setOpenJobDetailsModal(true);
    // Put the job id in the URL so the browser's address bar (and any "copy
    // link" action) points back to this specific job, not the bare board.
    navigateToJobSearch(navigate, job.id, { replace: true });
  };

  const { profile } = useProfileStore();

  const canCreateJobs = useHasPermission('create_jobs');

  if (isCreating) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-col md:min-h-[calc(100vh-4rem)] md:px-5 lg:px-32">
        <div className="flex w-full flex-col pt-4 md:w-2/3 md:pr-8">
          <h1 className="text-5xl font-bold text-secondary">{t('findOffer', { ns: 'jobs' })}</h1>
        </div>

        <div className="my-2 md:my-4">
          <div className="mb-4 md:mb-6">
            <SearchBar
              value={project.keyword}
              onChange={value => setProjectKeyword(value)}
              suggestions={suggestions}
              onSelect={suggestion => {
                setProjectKeyword(suggestion);
                handleSearchSubmit();
              }}
              onSubmit={handleSearchSubmit}
              onFetchSuggestions={fetch_job_suggestions}
              loading={suggestionsLoading}
              placeholder={t('navigation.searchPlaceholder', { ns: 'common' })}
              className="w-full max-w-2xl"
            />
          </div>
          <JobSearchFilterToolbar
            onApplySearch={handleSearchSubmit}
            labels={jobSearchToolbarLabels}
            getLanguageDisplayLabel={code =>
              languageOptions.find(opt => opt.code === code)?.label || code
            }
            selectedLanguages={selectedLanguages}
            selectedServices={selectedServices}
            selectedCities={selectedCities}
            alphabeticalSort={alphabeticalSort}
            publishedAtSort={publishedAtSort}
            onRemoveLanguage={async code => {
              const newLanguages = selectedLanguages.filter(l => l !== code);
              setSelectedLanguages(newLanguages);
              await search_jobs({
                keyword: project.keyword.trim() || undefined,
                filter_languages: newLanguages,
                filter_city_ids: selectedCities.map(c => c.id),
                filter_service_category_slugs: selectedServices.map(s => s.slug),
                sort_order: buildJobSortOrder(alphabeticalSort, publishedAtSort),
              });
            }}
            onRemoveService={async id => {
              const newServices = selectedServices.filter(s => s.id !== id);
              setSelectedServices(newServices);
              await search_jobs({
                keyword: project.keyword.trim() || undefined,
                filter_languages: selectedLanguages,
                filter_city_ids: selectedCities.map(c => c.id),
                filter_service_category_slugs: newServices.map(s => s.slug),
                sort_order: buildJobSortOrder(alphabeticalSort, publishedAtSort),
              });
            }}
            onRemoveCity={async id => {
              const newCities = selectedCities.filter(c => c.id !== id);
              setSelectedCities(newCities);
              await search_jobs({
                keyword: project.keyword.trim() || undefined,
                filter_languages: selectedLanguages,
                filter_city_ids: newCities.map(c => c.id),
                filter_service_category_slugs: selectedServices.map(s => s.slug),
                sort_order: buildJobSortOrder(alphabeticalSort, publishedAtSort),
              });
            }}
            onRemoveAlphabeticalSortBadge={async () => {
              setAlphabeticalSort(null);
              const state = useSearchStateStore.getState().project;
              await search_jobs({
                keyword: state.keyword.trim() || undefined,
                filter_languages: state.selectedLanguages,
                filter_city_ids: state.selectedCities.map(city => city.id),
                filter_service_category_slugs: state.selectedServices.map(service => service.slug),
                sort_order: buildJobSortOrder(null, publishedAtSort),
              });
            }}
            onRemovePublishedAtSortBadge={async () => {
              setPublishedAtSort('publishedAtDesc');
              const state = useSearchStateStore.getState().project;
              await search_jobs({
                keyword: state.keyword.trim() || undefined,
                filter_languages: state.selectedLanguages,
                filter_city_ids: state.selectedCities.map(city => city.id),
                filter_service_category_slugs: state.selectedServices.map(service => service.slug),
                sort_order: buildJobSortOrder(state.alphabeticalSort, 'publishedAtDesc'),
              });
            }}
            components={{ Button: JobToolbarButton, Badge: JobToolbarBadge }}
            renderFilterModal={({ className, isBlocking, onClose, onSubmit }) => (
              <FilterModal
                className={className}
                isBlocking={isBlocking}
                onClose={onClose}
                onSubmit={onSubmit}
                selectedLanguages={selectedLanguages}
                setSelectedLanguages={setSelectedLanguages}
                selectedServices={selectedServices}
                setSelectedServices={setSelectedServices}
                selectedCities={selectedCities}
                setSelectedCities={setSelectedCities}
              />
            )}
            renderSortModal={({ className, isBlocking, onClose, onSubmit }) => (
              <JobSortModal
                className={className}
                isBlocking={isBlocking}
                onClose={onClose}
                onSubmit={onSubmit}
                alphabeticalSort={alphabeticalSort}
                setAlphabeticalSort={setAlphabeticalSort}
                publishedAtSort={publishedAtSort}
                setPublishedAtSort={setPublishedAtSort}
              />
            )}
            renderToolbarAccessory={({ desktopModalsOpen }) =>
              canCreateJobs && !desktopModalsOpen ? (
                <Button
                  href="/jobs"
                  state={{ openCreate: true }}
                  type="button"
                  variant="simple-text"
                  className="w-auto w-full text-left transition duration-300 ease-in-out hover:bg-gray-200 md:ml-auto md:px-8"
                >
                  {t('create_offer_button', { ns: 'jobs' })}
                </Button>
              ) : null
            }
          />

          <section className="mt-2 flex flex-col gap-6">
            {publishedJobs.map(job => (
              <div key={job.id}>
                <JobSearchCard job={job} onSelect={onSelectjob} />
                <div className="mx-10 border border-t"></div>
              </div>
            ))}
          </section>

          {searchError && (
            <div className="mt-4 flex flex-col items-center justify-center rounded-xl border border-red-200 bg-red-50 px-4 py-12">
              <p className="mb-2 font-semibold text-red-700">
                {t('searchErrorMessage', { ns: 'jobs' })}
              </p>
              <button
                className="mt-2 rounded bg-red-600 px-4 py-2 text-white transition hover:bg-red-700"
                onClick={handleSearchSubmit}
              >
                {t('retry', { ns: 'jobs' })}
              </button>
              <p className="mt-2 text-xs text-red-500">{t('contactSupport', { ns: 'jobs' })}</p>
            </div>
          )}

          {publishedJobs.length === 0 && (
            <div className="flex flex-col items-center justify-center px-4 py-12 text-center">
              <p className="mb-2 text-lg font-semibold text-gray-500">
                {t('noResults', { ns: 'jobs' })}
              </p>
              <p className="text-sm text-gray-400">{t('refineSearch', { ns: 'jobs' })}</p>
            </div>
          )}

          {hasMore && (
            <div>
              <Button
                className="my-4 w-full md:w-auto md:justify-self-end md:px-8"
                onClick={() => fetch_more_jobs()}
              >
                {t('loadMore', { ns: 'jobs' })}
              </Button>
            </div>
          )}
        </div>
      </div>

      {selectedJob && openJobDetailsModal && (
        <JobDetailsModal
          job={selectedJob}
          onClose={() => {
            setOpenJobDetailsModal(false);
            navigate(location.pathname, { replace: true });
          }}
          onApply={() => {
            setOpenJobDetailsModal(false);
            handleCreateQuote();
          }}
          onClick={() => {
            setShowJobCancelConfirmationModal(true);
          }}
        />
      )}

      {openApplyModal && selectedJob && profile && createdQuote && (
        <UpsertQuoteModal
          onClose={() => {
            setOpenApplyModal(false);
            setCreatedQuoteId(null);
          }}
          job={selectedJob}
          quote={createdQuote}
        />
      )}

      {showErrorModal && selectedJob && (
        <GenericInformationModal
          title={t('createQuoteErrorTitle', 'Erreur lors de la création')}
          message={t('createQuoteErrorHint', { ns: 'jobs' })}
          onClose={() => setShowErrorModal(false)}
          onConfirm={handleRetryCreateQuote}
          closeButtonLabel={t('retry', { ns: 'jobs' })}
          confirmButtonLabel={t('retry', { ns: 'jobs' })}
        />
      )}

      {showJobCancelConfirmationModal && (
        <GenericInformationModal
          title={t('cancel_this_job', { ns: 'admin' })}
          message={t('cancel_this_hint', { ns: 'admin' })}
          onClose={() => setShowJobCancelConfirmationModal(false)}
          onConfirm={handleCancelJob}
          closeButtonLabel={t('cancel_changes', { ns: 'common' })}
          confirmButtonLabel={t('common:form_actions.confirm', { ns: 'common' })}
        />
      )}
    </>
  );
};

export default JobSearchPage;
